import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, ArrowRight } from "lucide-react";
import { getActivePartnerships } from "../../services/partnershipsApi";
import { getPhoto } from "../../storageService";
import "../Providers/PartnerCard.css";
import "./PartnerPopup.css";

const SESSION_FLAG = "hasSeenPartnerPopup";
const ADV_FLAG = "hasSeenAdvertisementPopup";
const ADV_GRACE_MS = 4000;

const PartnerPopup = () => {
    const navigate = useNavigate();
    const [partnerships, setPartnerships] = useState([]);
    const [photoUrls, setPhotoUrls] = useState({});
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem(SESSION_FLAG) === "true") return;

        let cancelled = false;

        // Esperamos al AdvertisementPopup: si ya se vio o pasan ADV_GRACE_MS, avanzamos.
        const waitForAdvertisement = () => new Promise((resolve) => {
            const start = Date.now();
            const tick = () => {
                if (cancelled) return resolve(false);
                if (sessionStorage.getItem(ADV_FLAG) === "true") return resolve(true);
                if (Date.now() - start >= ADV_GRACE_MS) return resolve(false);
                setTimeout(tick, 250);
            };
            tick();
        });

        (async () => {
            try {
                await waitForAdvertisement();
                if (cancelled) return;

                const all = await getActivePartnerships();
                if (cancelled) return;

                // Filtramos por popupConfig.enabled — el admin decide cuáles aparecen acá.
                const eligible = (all || []).filter((p) => p?.popupConfig?.enabled);
                if (eligible.length === 0) return;

                // Resolver URLs en paralelo: priorizamos popupConfig.image → partnerPhoto → bannerPhoto
                const entries = await Promise.all(
                    eligible.map(async (p) => {
                        const path = p.popupConfig?.image || p.partnerPhoto || p.bannerPhoto;
                        if (!path) return [p.id, null];
                        try { return [p.id, await getPhoto(path)]; }
                        catch { return [p.id, null]; }
                    })
                );
                if (cancelled) return;

                setPhotoUrls(Object.fromEntries(entries));
                setPartnerships(eligible);
                setIsVisible(true);
            } catch (err) {
                console.warn("[Partner Popup] error cargando partnerships:", err);
            }
        })();

        return () => { cancelled = true; };
    }, []);

    // Posiciones y animaciones randomizadas por partnership — memoized para no
    // re-randomizar en cada render (sería un mareo visual).
    const floatStyles = useMemo(() => {
        const rand = (min, max) => min + Math.random() * (max - min);
        return partnerships.map(() => ({
            top: `${rand(8, 70)}%`,
            left: `${rand(6, 80)}%`,
            animationDuration: `${rand(7, 14).toFixed(2)}s`,
            animationDelay: `${rand(0, 4).toFixed(2)}s`,
            // Variación leve de tamaño para sensación orgánica
            width: `${rand(46, 72).toFixed(0)}px`,
        }));
    }, [partnerships]);

    const handleClose = () => {
        setIsVisible(false);
        sessionStorage.setItem(SESSION_FLAG, "true");
    };

    const handleGoToPartners = () => {
        sessionStorage.setItem(SESSION_FLAG, "true");
        setIsVisible(false);
        navigate("/partners");
    };

    const handleBackdropClick = (e) => {
        if (e.target.classList.contains("partner-popup-backdrop")) handleClose();
    };

    if (!isVisible || partnerships.length === 0) return null;

    return (
        <div className="partner-popup-backdrop" onClick={handleBackdropClick}>
            <div className="partner-popup" role="dialog" aria-modal="true" aria-labelledby="partner-popup-title">
                <button
                    type="button"
                    className="partner-popup__close"
                    onClick={handleClose}
                    aria-label="Cerrar"
                >
                    <X size={20} />
                </button>

                <div
                    className="partner-popup__float-area"
                    onClick={handleGoToPartners}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleGoToPartners(); }}
                    aria-label="Ver todos los partners"
                >
                    {partnerships.map((p, i) => {
                        const url = photoUrls[p.id];
                        const name = p.partnerName || p.productProviderId;
                        const style = floatStyles[i] || {};
                        return url ? (
                            <img
                                key={p.id}
                                src={url}
                                alt={name}
                                title={name}
                                className="partner-popup__float-logo"
                                style={style}
                                loading="lazy"
                            />
                        ) : (
                            <div
                                key={p.id}
                                className="partner-popup__float-logo partner-popup__float-logo--plain"
                                style={style}
                                title={name}
                                aria-hidden="true"
                            >
                                {name?.[0]?.toUpperCase() || "?"}
                            </div>
                        );
                    })}
                </div>

                <div className="partner-popup__body">
                    <span className="partner-popup__eyebrow">
                        <Sparkles size={13} />
                        Partners de <span className="brand">CONTACTAME</span>
                    </span>
                    <h2 id="partner-popup-title" className="partner-popup__title">
                        Beneficios exclusivos para nuestros profesionales
                    </h2>
                    <p className="partner-popup__description">
                        Un partner por categoría ofrece <b>descuentos y beneficios exclusivos</b> a
                        todos los profesionales registrados en <span className="brand">CONTACTAME</span>.
                    </p>
                    <button
                        type="button"
                        className="partner-popup__cta"
                        onClick={handleGoToPartners}
                    >
                        Ver todos los partners
                        <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PartnerPopup;
